import { GRPCServices } from "../grpc/grpcapi";
import { ProjectService } from "../project/ProjectService";
import { DialogService } from "../core/dialog/GlobalDialogState";
import { ProgressService } from "../core/progress/GlobalProgressState";
import { DeploymentService } from "../deployment/DeploymentService";
import { makeAutoObservable } from "mobx";
import { ServerData } from "../core/data/ServerData";
import { GalaxyMigrateStorageConfig } from "gc-web-proto/galaxycompletepb/apipb/domainpb/galaxymigrate_pb";
import {
    ConfigureCdcRelayServer,
    CreateGalaxyMigrateLink,
    GetCdcRelayServerStatus,
    GetGMStorageConfig,
    GetIOStats,
    GetXRay,
    ListGalaxyMigrateLinks,
    ReconnectGalaxyMigrateLink,
    RefreshGalaxyMigrateLink,
    RemoveGalaxyMigrateLink,
    RestartMTdiDaemon,
    RestartGalaxyMigrate,
    ViewLogs,
} from "gc-web-proto/galaxycompletepb/apipb/gmapipb/galaxymigrate_api_pb";
import { CdcRelayServerConfig, ViewLogsRequest } from "gc-web-proto/galaxymigratepb/galaxy_migrate_types_pb";
import { PagerParams } from "gc-web-proto/galaxycompletepb/commonpb/datafilter_pb";
import { ServerListData } from "../core/data/ListData";
import { GMLinkInfo } from "gc-web-proto/galaxycompletepb/apipb/domainpb/galaxymigratelink_pb";
import { IOStatsDataSet } from "gc-web-proto/galaxymigratepb/galaxy_migrate_types_pb";
import { IO_TYPE } from "./performance/GalaxyMigrateDeploymentPerformanceHelpers";
import { appI18n } from "../i18n/i18n";

export class GalaxyMigrateDeploymentService {
    protected readonly api: GRPCServices;
    private readonly projectService: ProjectService;
    private readonly dialogService: DialogService;
    private readonly progressService: ProgressService;

    protected readonly deploymentService: DeploymentService;

    storageConfig = new ServerData<GalaxyMigrateStorageConfig>().setDataFetcher(this.fetchStorageConfig.bind(this));

    deploymentLogs = new ServerData<ViewLogs.Response>().setDataFetcher(this.fetchDeploymentLogs.bind(this));

    galaxyMigrateLinks = new ServerListData<ListGalaxyMigrateLinks.Response, GMLinkInfo>().setDataFetcher(this.fetchGalaxyMigrateLinks.bind(this));

    cdcRelayServerStatus = new ServerData<GetCdcRelayServerStatus.Response.AsObject>().setDataFetcher(this.getRelayServerStatus.bind(this));

    constructor(
        api: GRPCServices,
        projectService: ProjectService,
        dialogService: DialogService,
        progressService: ProgressService,
        deploymentService: DeploymentService
    ) {
        this.api = api;
        this.projectService = projectService;
        this.dialogService = dialogService;
        this.progressService = progressService;
        this.deploymentService = deploymentService;

        makeAutoObservable(this);
    }

    // represent current deployment

    async fetchStorageConfig(deploymentId?: string, includeLvm = false) {
        deploymentId = deploymentId || this.deploymentService.currentDeploymentID;
        const req = new GetGMStorageConfig.Request().setSystemId(deploymentId).setIncludeLvm(includeLvm);

        const res = await this.api.gmService.getStorageConfig(req, null);
        return res.getStorageConfig();
    }

    async assignUnlimitedLicense(systemId?: string) {
        const sysId = systemId || this.deploymentService.currentDeploymentID;
        //const req   = new AssignUnlimitedLicense.Request()
        //.setSystemId(sysId);
        //return await this.progressService.track(this.api.gmService.assignUnlimitedLicense(req, null));
    }

    async fetchDeploymentLogs(deploymentId = this.deploymentService.currentDeploymentID, source: ViewLogsRequest.LogSource, filter: string) {
        if (!deploymentId) {
            throw new Error(appI18n.t("galaxymigrate:errors.missingDeploymentContext"));
        }

        const req = new ViewLogs.Request().setSystemId(deploymentId).setParams(new ViewLogsRequest().setSource(source).setLimit(1000).setFilter(filter));

        return await this.api.gmService.viewLogs(req, null);
    }

    async getMigrationXRay(systemId?: string, argumentsList?: string[]) {
        systemId = systemId || this.deploymentService.currentDeploymentID;
        const req = new GetXRay.Request().setSystemId(systemId);

        if (!!argumentsList) {
            req.setArgumentsList(argumentsList);
        }
        return await this.progressService.track(this.api.gmService.getXRay(req, null), appI18n.t("galaxymigrate:progress.generatingXray"));
    }

    async restartMTdiDaemon(systemId?: string) {
        systemId = systemId || this.deploymentService.currentDeploymentID;
        const req = new RestartMTdiDaemon.Request().setSystemId(systemId);
        return await this.progressService.track(this.api.gmService.restartMTdiDaemon(req, null), appI18n.t("galaxymigrate:progress.restartingMtdi"));
    }
    async restartGalaxyMigrate(systemId?: string) {
        systemId = systemId || this.deploymentService.currentDeploymentID;
        const req = new RestartGalaxyMigrate.Request().setSystemId(systemId);
        return await this.progressService.track(
            this.api.gmService.restartGalaxyMigrate(req, null),
            appI18n.t("galaxymigrate:progress.restartingGalaxyMigrate")
        );
    }

    async createDeploymentLink(
        serverId: string,
        clientId: string,
        serverAddress: string,
        serverPort: number = null,
        description: string,
        allowProxy: boolean,
        compression: boolean,
        relayAddress?: string,
        relayPort?: number
    ) {
        const req = new CreateGalaxyMigrateLink.Request()
            .setServersystemid(serverId)
            .setClientsystemid(clientId)
            .setServeraddress(serverAddress)
            .setServerport(serverPort)
            .setDescription(description)
            .setAllowProxy(allowProxy)
            .setCompression(compression);

        if (relayAddress) {
            req.setRelayAddress(relayAddress);
        }
        if (relayPort) {
            req.setRelayPort(relayPort);
        }

        return await this.progressService.track(this.api.gmService.createGalaxyMigrateLink(req, null), appI18n.t("galaxymigrate:progress.creatingConnection"));
    }

    async batchCreateDeploymentLinks(
        array: Array<{
            server: { serverId: string; serverName: string };
            client: { clientId: string; clientName: string };
            serverAddress: string;
            serverPort: number;
            description: string;
            compression: boolean;
            proxy: boolean;
            relayAddress?: string;
            relayPort?: number;
        }>
    ) {
        for (let link of array) {
            if (link.relayAddress) {
                if (link.relayAddress.includes(":")) {
                    const [address, port] = link.relayAddress.split(":");
                    link.relayAddress = address;
                    link.relayPort = parseInt(port);
                }
            }
            await this.createDeploymentLink(
                link.server.serverId,
                link.client.clientId,
                link.serverAddress,
                link.serverPort,
                link.description,
                link.proxy,
                link.compression,
                link.relayAddress,
                link.relayPort
            );
        }
    }

    async fetchGalaxyMigrateLinks(systemId: string = "", onlyConnected: boolean = false, pagerParams?: PagerParams) {
        const req = new ListGalaxyMigrateLinks.Request()
            .setProjectId(this.projectService.currentProjectID)
            .setOnlyConnected(onlyConnected)
            .setSystemIdFilter(systemId)
            .setPageParams(pagerParams || this.galaxyMigrateLinks.pagerParam);
        return await this.api.gmService.listGalaxyMigrateLinks(req, null);
    }

    async removeGalaxyMigrateLink(linkId: string) {
        const req = new RemoveGalaxyMigrateLink.Request().setLinkid(linkId);
        const confirmed = await this.dialogService.addConfirmDialog({
            message: appI18n.t("galaxymigrate:links.removeConfirmation"),
            autoConfirmationQuestionLine: false,
        });
        if (confirmed) {
            return await this.progressService.track(this.api.gmService.removeGalaxyMigrateLink(req, null));
        }
    }

    async reconnectGalaxyMigrateLink(linkId: string, connectionAddress: string, serverPort: number = null, compression: boolean, proxy: boolean) {
        const req = new ReconnectGalaxyMigrateLink.Request()
            .setLinkid(linkId)
            .setServeraddress(connectionAddress)
            .setServerport(serverPort)
            .setCompression(compression)
            .setAllowProxy(proxy);

        return await this.progressService.track(this.api.gmService.reconnectGalaxyMigrateLink(req, null));
    }

    async refreshGalaxyMigrateLink(linkId: string) {
        const req = new RefreshGalaxyMigrateLink.Request().setLinkid(linkId);
        return await this.progressService.track(this.api.gmService.refreshGalaxyMigrateLink(req, null));
    }

    async configureRelayServer(address: string, enable: boolean) {
        const req = new ConfigureCdcRelayServer.Request()
            .setSystemId(this.deploymentService.currentDeploymentID)
            .setConfig(new CdcRelayServerConfig().setEnable(enable).setAddress(address));

        return await this.progressService.track(this.api.gmService.configureCdcRelayServer(req, null));
    }

    async getRelayServerStatus() {
        const req = new GetCdcRelayServerStatus.Request().setSystemId(this.deploymentService.currentDeploymentID);

        const response = await this.api.gmService.getCdcRelayServerStatus(req, null);
        return response.toObject();
    }

    async getIoStats(numOfPoints: number, domainsList: Array<string[]>, ioType: IO_TYPE) {
        const includeRead = !(ioType === IO_TYPE.WRITE);
        const includeWrite = !(ioType === IO_TYPE.READ);
        const timeDomain = new IOStatsDataSet.TimeDomain().setPoints(numOfPoints).setLive(false);

        if (numOfPoints === 300) {
            timeDomain.setLive(true);
        }

        const domains =
            domainsList.length > 0
                ? domainsList.map((domain) => {
                      return new IOStatsDataSet.Domain().setDevicePathsList(domain).setIncludeWrites(includeWrite).setIncludeReads(includeRead);
                  })
                : [new IOStatsDataSet.Domain().setDevicePathsList([]).setIncludeReads(includeRead).setIncludeWrites(includeWrite)];

        const req = new GetIOStats.Request().setTimeDomain(timeDomain).setDomainsList(domains).setSystemId(this.deploymentService.currentDeploymentID);

        const res = await this.api.gmService.getIOStats(req, null);
        return res.toObject();

        //return new GetIOStatsResponse().toObject()
    }
}
